import {createDsScoutingClient} from "@/api/ds_scouting";
import {GPS_STAT_METRICS} from "@/config/matchGpsStats";
import {rosterMeasurementAverage} from "@/utils/rosterCondition";
import moment from "moment";
import {summarizeMatrixRow} from "@/utils/rosterActivityMatrix";
import {
	getDsContext,
	onDsContextChange,
	isDsContextReady,
	assertDsContext,
	readDsPreference,
	writeDsPreference,
} from "@/utils/dsContext";

import {ContractAPI} from "@/api/contract";
import {FeeAPI} from "@/api/fee";
import {MatchAPI} from "@/api/match";
import {MedicalAPI, isInjuryActiveAt} from "@/api/medical";
import {PlayerAPI} from "@/api/player";
import {RosterAPI} from "@/api/roster";
import {ScoutingReportAPI} from "@/api/scouting_report";
import {StaffAPI} from "@/api/staff";
import {TrainingAPI} from "@/api/training";
import {UserAPI} from "@/api/user";
import {VenueAPI} from "@/api/venue";
import {certificateStatus} from "@/utils/certificates";
import {
	getRosterName,
	isInternalPlayer,
	visibleRosters,
	rosterStaffRole,
	rosterStaffPriority,
} from "@/utils/functions";
import {getInternalClubId} from "@/utils/constants";
import {demoFees} from "@/utils/clubDemo";

// ── Fotografia del club per la vista DS ─────────────────────────────────────
// Un unico caricamento cross-area (rose, gare, medica, contratti, wellness,
// staff, quote) condiviso da tutte le pagine /ds, con cache breve per non
// ripetere le chiamate a ogni cambio pagina.

const CACHE_TTL_MS = 3 * 60 * 1000;
let snapshotCache = null;
let certificatesCache = null;
let agentsCache = null;
onDsContextChange(() => {
	snapshotCache = null;
	certificatesCache = null;
	agentsCache = null;
});

const BUDGET_KEY = "finance_budget_v1";
const MOVEMENTS_KEY = "finance_movements_v1";

export const PIPELINE_STAGES = [
	{id: "watching", label: "Monitorati"},
	{id: "evaluating", label: "In valutazione"},
	{id: "target", label: "Obiettivi"},
	{id: "negotiating", label: "In trattativa"},
];

// Reparti con soglia minima consigliata per una rosa agonistica
export const DEPARTMENTS = [
	{id: "gk", label: "Portieri", singular: "Portiere", short: "P", min: 2},
	{id: "def", label: "Difensori", singular: "Difensore", short: "D", min: 6},
	{
		id: "mid",
		label: "Centrocampisti",
		singular: "Centrocampista",
		short: "C",
		min: 6,
	},
	{id: "att", label: "Attaccanti", singular: "Attaccante", short: "A", min: 4},
];

export const getDepartment = (player) => {
	if (player?.goalkeeper || player?.primaryRole === "Portiere") return "gk";
	if (player?.primaryRole === "Difensore") return "def";
	if (
		["Mediano", "Centrocampista", "Trequartista"].includes(player?.primaryRole)
	)
		return "mid";
	if (player?.primaryRole === "Attaccante") return "att";
	return null;
};

export const playerName = (p) =>
	`${p?.firstName || ""} ${p?.lastName || ""}`.trim() || "—";

export const formatEuro = (n) =>
	n == null || isNaN(n)
		? "—"
		: "€ " + Number(n).toLocaleString("it-IT", {maximumFractionDigits: 0});

const mean = (values) => {
	const valid = values.filter((v) => v !== null && v !== undefined);
	return valid.length ? valid.reduce((s, v) => s + v, 0) / valid.length : null;
};

export const staffRoleName = (member) =>
	rosterStaffRole({
		...member,
		role: typeof member?.role === "string" ? member.role : member?.role?.name,
	}) || "Ruolo non indicato";

export const normalizeDsRosterStaff = (assignments) =>
	(assignments || [])
		.map((entry) => {
			const person = entry.staff || entry;
			const assignment = entry.staff ? entry : {staff: person};
			return {
				...person,
				staff: person,
				role: rosterStaffRole(assignment),
				assignmentRole: entry.staff ? entry.role : null,
				assignmentId: entry.staff ? entry.id ?? null : null,
			};
		})
		.filter((member) => member.id)
		.sort((a, b) => rosterStaffPriority(a) - rosterStaffPriority(b));

const injuryElapsedDays = (injury, now) =>
	injury.startDate
		? Math.max(0, now.diff(moment(injury.startDate), "days"))
		: null;

const feeTotals = (fees, now) => {
	let expected = 0;
	let paid = 0;
	let overdue = 0;
	const overduePlayers = new Set();
	(fees || []).forEach((fee) => {
		expected += parseFloat(fee.totalAmount) || 0;
		(fee.installments || []).forEach((i) => {
			const amount = parseFloat(i.amount) || 0;
			if (i.status === "paid") paid += amount;
			else if (i.dueDate && moment(i.dueDate).isBefore(now, "day")) {
				overdue += amount;
				overduePlayers.add(fee.playerId);
			}
		});
	});
	return {
		count: (fees || []).length,
		expected,
		paid,
		overdue,
		overduePlayers: overduePlayers.size,
	};
};

// Le proiezioni API applicano MatchResultCalculator anche ai campi *ClubGoals.
// Se result è presente è autorevole, inclusi i null di un automatico sospeso.
export const matchResultDetails = (match) =>
	match?.resultDetails ||
	(typeof match?.result === "object" ? match.result : null);

export const matchGoals = (match) => {
	const result = matchResultDetails(match);
	return result
		? [result.home ?? null, result.away ?? null]
		: [match?.homeClubGoals ?? null, match?.awayClubGoals ?? null];
};

export const hasMatchScore = (match) =>
	matchGoals(match).every(Number.isInteger);

const validMatchDate = (match) =>
	!!match?.startDate && moment(match.startDate).isValid();

// Stessa distinzione temporale del profilo canonico: OdD oggi resta programmata.
export const isMatchScheduled = (match, now = moment()) =>
	validMatchDate(match) &&
	(match.startTimeToBeConfirmed
		? moment(match.startDate).isSameOrAfter(now, "day")
		: moment(match.startDate).isAfter(now));

// Stessa selezione della prossima partita di RosterPanorama.
export const isMatchUpcoming = (match, now = moment()) =>
	validMatchDate(match) &&
	!hasMatchScore(match) &&
	(moment(match.startDate).isAfter(now) ||
		(match.startTimeToBeConfirmed &&
			moment(match.startDate).isSame(now, "day")));

export const matchDateLabel = (match, pattern = "ddd D/MM") =>
	validMatchDate(match)
		? moment(match.startDate).format(pattern) +
			(match.startTimeToBeConfirmed
				? " · OdD"
				: " · " + moment(match.startDate).format("HH:mm"))
		: "Data non disponibile";

const matchDaysUntil = (match, now = moment()) =>
	validMatchDate(match)
		? moment(match.startDate)
				.startOf("day")
				.diff(now.clone().startOf("day"), "days")
		: null;

export const matchCountdown = (match, now = moment()) => {
	const days = matchDaysUntil(match, now);
	return days === null
		? "Data non disponibile"
		: days === 0
			? "Oggi"
			: days === 1
				? "Domani"
				: `Tra ${days} giorni`;
};

export const matchResultWarning = (match, now = moment()) => {
	const result = matchResultDetails(match);
	if (result?.warning) return result.warning;
	if (result?.conflict || result?.source === "conflict")
		return "Risultato da confrontare: verifica i dati nella gara completa.";
	if (!validMatchDate(match)) return "Data della gara non disponibile.";
	if (isMatchScheduled(match, now) && hasMatchScore(match))
		return "Risultato presente su una gara programmata: escluso dal rendimento.";
	const goals = matchGoals(match);
	if (goals.some((value) => value !== null) && !hasMatchScore(match))
		return "Risultato parziale o non valido: escluso dal rendimento.";
	return null;
};

export const matchRosterSide = (match, rosterId) => {
	const home = match.homeRosterId === rosterId;
	const away = match.awayRosterId === rosterId;
	return home === away ? null : home ? "home" : "away";
};

export const summarizeTeamMatches = (matches, rosterId, now = moment()) => {
	const teamMatches = (matches || [])
		.filter(
			(match) =>
				match.homeRosterId === rosterId || match.awayRosterId === rosterId,
		)
		.sort((a, b) => moment(a.startDate).diff(moment(b.startDate)));
	const played = teamMatches
		.filter(
			(match) =>
				validMatchDate(match) &&
				!isMatchScheduled(match, now) &&
				hasMatchScore(match) &&
				!matchResultDetails(match)?.conflict &&
				matchResultDetails(match)?.source !== "conflict" &&
				!matchResultDetails(match)?.warning &&
				matchRosterSide(match, rosterId),
		)
		.map((match) => {
			const home = matchRosterSide(match, rosterId) === "home";
			const [homeGoals, awayGoals] = matchGoals(match);
			const gf = home ? homeGoals : awayGoals;
			const ga = home ? awayGoals : homeGoals;
			return {
				...match,
				resultDetails: matchResultDetails(match),
				gf,
				ga,
				home,
				opponent: home
					? match.awayTeamName || match.awayClub
					: match.homeTeamName || match.homeClub,
				result: gf > ga ? "V" : gf < ga ? "P" : "N",
			};
		});
	const upcoming = teamMatches.filter((match) => isMatchUpcoming(match, now));
	const playedIds = new Set(played.map((match) => match.id));
	const missingResults = teamMatches.filter(
		(match) =>
			validMatchDate(match) &&
			!isMatchScheduled(match, now) &&
			!playedIds.has(match.id),
	);
	const resultIssues = teamMatches.flatMap((match) => {
		const warning =
			matchResultWarning(match, now) ||
			(!matchRosterSide(match, rosterId)
				? "Lato della rosa non univoco: verifica la gara completa."
				: null);
		return warning ? [{...match, warning}] : [];
	});
	const record = played.reduce(
		(acc, match) => {
			acc[match.result] += 1;
			acc.gf += match.gf;
			acc.ga += match.ga;
			return acc;
		},
		{V: 0, N: 0, P: 0, gf: 0, ga: 0},
	);
	const nextMatch = upcoming[0] || null;
	return {
		played,
		upcoming,
		missingResults,
		resultIssues,
		record,
		form: played.slice(-5).map((match) => match.result),
		nextMatch,
		daysToMatch: nextMatch ? matchDaysUntil(nextMatch, now) : null,
	};
};

export const summarizeTrainingAttendance = (matrix) => {
	const completed = (matrix.columns || []).filter(
		(column) => !column.scheduled,
	);
	const keys = new Set(completed.map((column) => column.key));
	const rows = (matrix.players || []).map((player) => {
		const cells = Object.fromEntries(
			Object.entries(player.cells || {}).filter(([key]) => keys.has(key)),
		);
		const summary = summarizeMatrixRow({...player, cells}, "training");
		const states = Object.values(cells);
		return {
			...summary,
			playerId: player.playerId,
			name: player.name,
			guest: player.guest,
			absent: states.filter((cell) => cell.status === "absent").length,
			injured: states.filter((cell) => cell.status === "injured").length,
		};
	});
	const total = rows.reduce((sum, row) => sum + row.total, 0);
	const attended = rows.reduce((sum, row) => sum + row.attended, 0);
	const unknown = rows.reduce((sum, row) => sum + row.unknown, 0);
	return {
		fromDate: matrix.fromDate,
		toDate: matrix.toDate,
		sessions: completed.length,
		scheduled: (matrix.columns || []).filter((column) => column.scheduled)
			.length,
		total,
		attended,
		unknown,
		known: total - unknown,
		rate: total ? attended / total : null,
		absent: rows.reduce((sum, row) => sum + row.absent, 0),
		injured: rows.reduce((sum, row) => sum + row.injured, 0),
		rows,
		absentees: rows
			.filter((row) => row.absent > 0)
			.map((row) => ({
				player: {id: row.playerId, lastName: row.name},
				count: row.absent,
			})),
	};
};

export const summarizeAttendanceTeams = (teams) => {
	const summaries = teams.map((team) => team.organization.attendance);
	const total = summaries.reduce((sum, row) => sum + row.total, 0);
	const attended = summaries.reduce((sum, row) => sum + row.attended, 0);
	const unknown = summaries.reduce((sum, row) => sum + row.unknown, 0);
	return {
		total,
		attended,
		unknown,
		known: total - unknown,
		rate: total ? attended / total : null,
	};
};

export const attendanceCoverage = (attendance) =>
	attendance
		? attendance.attended +
			"/" +
			attendance.total +
			" presenze · " +
			attendance.known +
			"/" +
			attendance.total +
			" stati rilevati · " +
			attendance.unknown +
			" da verificare"
		: "Dati non disponibili";

export const summarizeDsWellness = (
	players,
	tqrRows,
	rpeRows,
	fromDate,
	toDate,
	now,
) => {
	const ids = new Set(players.map((player) => player.id));
	const read = (rows, selected, field, dateField) =>
		rosterMeasurementAverage(
			rows,
			selected,
			field,
			dateField,
			fromDate,
			toDate,
			now,
		);
	const tqr = read(tqrRows, ids, "tqrValue", "tqrDate");
	const rpe = read(rpeRows, ids, "rpe", "rpeDate");
	const rows = players.map((player) => {
		const own = new Set([player.id]);
		const ownTqr = read(tqrRows, own, "tqrValue", "tqrDate");
		const ownRpe = read(rpeRows, own, "rpe", "rpeDate");
		return {
			player,
			avgTQR: ownTqr.value,
			avgRPE: ownRpe.value,
			tqr: ownTqr,
			rpe: ownRpe,
		};
	});
	return {
		tqr,
		rpe,
		expected: ids.size,
		fromDate,
		toDate,
		players: rows,
		sourceTqr: tqrRows,
		sourceRpe: rpeRows,
		avgTQR: tqr.value,
		avgRPE: rpe.value,
	};
};

export const wellnessCoverage = (metric, expected) =>
	metric
		? metric.players +
			"/" +
			expected +
			" giocatori · " +
			metric.measurements +
			" rilevazioni"
		: "Seleziona una rosa";

export const indexDsMemberships = (teams) => {
	const playerById = new Map(),
		teamsByPlayer = new Map();
	for (const team of teams)
		for (const player of team.players) {
			playerById.set(player.id, player);
			if (!teamsByPlayer.has(player.id)) teamsByPlayer.set(player.id, []);
			if (!teamsByPlayer.get(player.id).some((entry) => entry.id === team.id))
				teamsByPlayer.get(player.id).push(team);
		}
	return {
		playerById,
		teamsByPlayer,
		totalPlayers: playerById.size,
		membershipCount: [...teamsByPlayer.values()].reduce(
			(sum, entries) => sum + entries.length,
			0,
		),
	};
};

export const dsMembershipLabel = (entry) =>
	(entry.teams || (entry.team ? [entry.team] : []))
		.map((team) => team.name)
		.join(" · ");

export const dsMatchCounts = (teams) => ({
	uniquePlayed: new Set(
		teams.flatMap((team) => team.played.map((match) => match.id)),
	).size,
	playedParticipations: teams.reduce(
		(sum, team) => sum + team.played.length,
		0,
	),
});

export const dsConvocationCount = (detail, rosterId, side) => {
	if (!detail || !side) return null;
	const rows = detail.matchConvocations || [];
	if (
		rows.some(
			(row) =>
				(!row.matchSide && !row.rosterId) ||
				(row.matchSide && !["home", "away"].includes(row.matchSide)),
		)
	)
		return null;
	return rows.filter((row) =>
		row.matchSide ? row.matchSide === side : row.rosterId === rosterId,
	).length;
};

// ── Caricamento ─────────────────────────────────────────────────────────────

const fetchSnapshot = async (season, context) => {
	const seasonId = season.id;
	const clubId = getInternalClubId();
	const now = moment();
	const today = now.format("YYYY-MM-DD");
	const weekAgo = now.clone().subtract(6, "days").format("YYYY-MM-DD");
	const attendanceFrom = now.clone().subtract(13, "days").format("YYYY-MM-DD");
	const seasonStart = season.startDate
		? moment(season.startDate).format("YYYY-MM-DD")
		: now.clone().subtract(10, "months").format("YYYY-MM-DD");
	const seasonEnd = season.endDate
		? moment(season.endDate).format("YYYY-MM-DD")
		: now.clone().add(10, "months").format("YYYY-MM-DD");

	// Una fonte negata o guasta impedisce sintesi numeriche ingannevoli.

	const [
		rostersRes,
		medicalRes,
		contractsRes,
		matchesRes,
		trainingsRes,
		staffRes,
		observedRes,
		venuesRes,
		scoutReportsRes,
		usersRes,
		scoutingRes,
	] = await Promise.all([
		RosterAPI.list({seasonId, clubId}),
		MedicalAPI.recordsList(),
		ContractAPI.expiring(36500),
		MatchAPI.list({
			fromDate: seasonStart,
			toDate: seasonEnd,
			matchObserved: false,
			clubId,
			seasonId,
			includeDetails: false,
		}),
		TrainingAPI.list({
			fromDate: now.clone().subtract(13, "days").format("YYYY-MM-DD"),
			toDate: now.clone().add(7, "days").format("YYYY-MM-DDTHH:mm:ss"),
			seasonId,
		}),
		StaffAPI.list({prospect: false, seasonId}),
		MatchAPI.list({
			fromDate: today,
			toDate: seasonEnd,
			matchObserved: true,
			seasonId,
			includeDetails: false,
		}),
		VenueAPI.list(),
		ScoutingReportAPI.list({seasonId}),
		UserAPI.list(),
		createDsScoutingClient().overview(seasonId, clubId),
	]);

	assertDsContext(context);
	const internalRosters = visibleRosters(
		(rostersRes || []).filter(
			(r) =>
				!r.prospect &&
				!r.playerCategory?.basicActivities &&
				(!r.clubId || r.clubId === clubId),
		),
	);

	// Gare dei prossimi 7 giorni: il dettaglio serve per le convocazioni
	const soonMatches = (matchesRes || []).filter(
		(m) =>
			isMatchUpcoming(m, now) &&
			moment(m.startDate).isBefore(now.clone().add(7, "days")),
	);
	const [rosterData, detailRows] = await Promise.all([
		Promise.all(
			internalRosters.map(async (roster) => {
				const [members, tqr, rpe, fees, rosterStaff, trainingMatrix] =
					await Promise.all([
						RosterAPI.getPlayers(roster.id),
						PlayerAPI.listTQR({
							rosterId: roster.id,
							seasonId,
							fromDate: weekAgo,
							toDate: today,
						}),
						PlayerAPI.listRPE({
							rosterId: roster.id,
							seasonId,
							fromDate: weekAgo,
							toDate: today,
							includePreparation: false,
						}),
						FeeAPI.list({seasonId, rosterId: roster.id})
							.then((data) => ({ok: true, data: data || []}))
							.catch((error) => {
								if (error?.response?.status === 404)
									return {ok: false, data: []};
								throw error;
							}),
						RosterAPI.getStaffs(roster.id),
						RosterAPI.activityMatrix(roster.id, {
							kind: "training",
							fromDate: attendanceFrom,
							toDate: today,
						}),
					]);
				return {roster, members, tqr, rpe, fees, rosterStaff, trainingMatrix};
			}),
		),
		Promise.all(
			soonMatches.map((m) =>
				MatchAPI.getById(m.id).then((detail) => [m.id, detail]),
			),
		),
	]);
	const matchDetails = new Map(detailRows);

	// ── Infortuni (indicizzati per giocatore) ──────────────────────────────
	const injuriesByPlayer = new Map();
	const seasonInjuries = [];
	(medicalRes || []).forEach((record) => {
		const pid = record.playerData?.id ?? record.playerId;
		if (!pid) return;
		(record.injuriesIllness || [])
			.filter((i) => !i.recordType)
			.forEach((i) => {
				const injury = {
					...i,
					recordId: record.id,
					playerId: pid,
					player: record.playerData,
				};
				if (isInjuryActiveAt(i, now)) {
					if (!injuriesByPlayer.has(pid)) injuriesByPlayer.set(pid, []);
					injuriesByPlayer.get(pid).push(injury);
				}
				if (
					!i.startDate ||
					!moment(i.startDate).isValid() ||
					(moment(i.startDate).isSameOrBefore(now) &&
						(!i.sportsDischargeDate ||
							!moment(i.sportsDischargeDate).isValid() ||
							moment(i.sportsDischargeDate).isAfter(seasonStart)))
				)
					seasonInjuries.push(injury);
			});
	});

	const contracts = normalizeDsFinanceContracts(contractsRes);

	const staff = staffRes || [];
	const matches = matchesRes || [];
	const trainings = trainingsRes || [];
	const pastTrainings = rosterData.flatMap(({roster, trainingMatrix}) =>
		(trainingMatrix.columns || [])
			.filter(
				(column) =>
					!column.scheduled &&
					moment(column.date).isSameOrAfter(weekAgo, "day"),
			)
			.map((column) => ({id: column.eventId, rosterId: roster.id})),
	);
	// Modulo quote non attivo sul backend → dati dimostrativi dichiarati
	const feesPreview = rosterData.every((r) => !r.fees.ok);

	// ── Squadre ────────────────────────────────────────────────────────────
	const teams = rosterData.map(
		({roster, members, tqr, rpe, fees, rosterStaff, trainingMatrix}) => {
			const internalMembers = (members || []).filter(
				(rp) => rp.player && isInternalPlayer(rp.player),
			);
			const players = internalMembers.map((rp) => rp.player);
			const recentJoins = internalMembers
				.filter(
					(rp) =>
						rp.creationDate &&
						moment(rp.creationDate).isAfter(now.clone().subtract(30, "days")),
				)
				.map((rp) => ({player: rp.player, date: rp.creationDate}));
			const playerIds = new Set(players.map((p) => p.id));

			const injured = players.filter((p) => injuriesByPlayer.has(p.id));
			const injuredIds = new Set(injured.map((p) => p.id));

			const departments = Object.fromEntries(
				DEPARTMENTS.map((d) => [d.id, {total: 0, available: 0}]),
			);
			players.forEach((p) => {
				const dep = getDepartment(p);
				if (!dep) return;
				departments[dep].total += 1;
				if (!injuredIds.has(p.id)) departments[dep].available += 1;
			});

			const ages = players
				.filter((p) => p.dateOfBirth)
				.map((p) => now.diff(moment(p.dateOfBirth), "years", true));

			const teamMatches = matches
				.filter(
					(m) => m.homeRosterId === roster.id || m.awayRosterId === roster.id,
				)
				.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
			const matchSummary = summarizeTeamMatches(teamMatches, roster.id, now);
			const {upcoming} = matchSummary;

			// ── Attività del team manager ─────────────────────────────────
			const sideOf = (m) => (m.homeRosterId === roster.id ? "home" : "away");
			const nextWeek = upcoming
				.filter((m) => matchDetails.has(m.id))
				.map((m) => {
					const detail = matchDetails.get(m.id);
					const side = matchRosterSide(m, roster.id);
					return {
						...m,
						side,
						daysTo: moment(m.startDate)
							.startOf("day")
							.diff(now.clone().startOf("day"), "days"),
						convocations: dsConvocationCount(detail, roster.id, side),
						timeToConfirm: !!m.startTimeToBeConfirmed,
						fieldMissing: side === "home" && !m.fieldName && !m.venueId,
					};
				});
			const incompleteSheets = teamMatches.filter(
				(m) =>
					moment(m.startDate).isBetween(
						now.clone().subtract(30, "days"),
						now,
					) &&
					(sideOf(m) === "home"
						? m.homeParticipationComplete === false
						: m.awayParticipationComplete === false),
			);
			const attendance = summarizeTrainingAttendance(trainingMatrix);

			const wellness = summarizeDsWellness(
				(members || [])
					.filter((entry) => entry.player)
					.map((entry) => entry.player),
				tqr,
				rpe,
				weekAgo,
				today,
				now,
			);
			const wellnessPlayers = wellness.players;

			const teamContracts = contracts.filter((c) => playerIds.has(c.playerId));
			const teamStaff = normalizeDsRosterStaff(rosterStaff);

			return {
				id: roster.id,
				name: getRosterName(roster),
				category: roster.playerCategory?.name,
				players,
				injured,
				available: players.length - injured.length,
				avgAge: mean(ages),
				departments,
				...matchSummary,
				recentJoins,
				organization: {nextWeek, incompleteSheets, attendance},
				wellness: {
					...wellness,
					lowTQR: wellnessPlayers.filter(
						(w) =>
							!injuredIds.has(w.player.id) &&
							w.avgTQR !== null &&
							w.avgTQR < 13,
					),
					highRPE: wellnessPlayers.filter(
						(w) => w.avgRPE !== null && w.avgRPE >= 8,
					),
				},
				trainingsWeek: pastTrainings.filter((t) => t.rosterId === roster.id)
					.length,
				contracts: teamContracts,
				...summarizeDsContractCosts(teamContracts),
				staff: teamStaff,
				fees: feeTotals(
					feesPreview ? demoFees(roster, players, seasonStart) : fees.data,
					now,
				),
			};
		},
	);

	const membershipIndex = indexDsMemberships(teams);
	const {playerById, teamsByPlayer} = membershipIndex;

	// ── Infortuni attivi del club ──────────────────────────────────────────
	const activeInjuries = [];
	const activeInjuryEpisodes = [];
	const medicalPlayerIds = new Set(
		(medicalRes || [])
			.map((record) => record.playerData?.id ?? record.playerId)
			.filter((pid) => playerById.has(pid)),
	);
	injuriesByPlayer.forEach((list, pid) => {
		const memberships = teamsByPlayer.get(pid) || [];
		const team = memberships.length === 1 ? memberships[0] : null;
		if (!memberships.length) return;
		const episodes = list
			.map((injury) => ({
				...injury,
				team,
				teams: memberships,
				player: injury.player || playerById.get(pid),
				elapsedDays: injuryElapsedDays(injury, now),
				daysToDischarge: injury.sportsDischargeDate
					? moment(injury.sportsDischargeDate)
							.startOf("day")
							.diff(now.clone().startOf("day"), "days")
					: null,
			}))
			.sort((a, b) => moment(b.startDate).diff(moment(a.startDate)));
		activeInjuryEpisodes.push(...episodes);
		activeInjuries.push({...episodes[0], episodes});
	});
	activeInjuries.sort((a, b) => (b.elapsedDays ?? 0) - (a.elapsedDays ?? 0));

	const clubSeasonInjuries = seasonInjuries.filter((i) =>
		playerById.has(i.playerId),
	);

	// ── Contratti ──────────────────────────────────────────────────────────
	const contractRows = contracts
		.filter((c) => playerById.has(c.playerId))
		.map((c) => ({
			...c,
			teams: teamsByPlayer.get(c.playerId) || [],
			team:
				teamsByPlayer.get(c.playerId)?.length === 1
					? teamsByPlayer.get(c.playerId)[0]
					: null,
			daysLeft: c.endDate
				? moment(c.endDate)
						.startOf("day")
						.diff(now.clone().startOf("day"), "days")
				: c.daysToExpiry ?? null,
		}))
		.sort((a, b) => (a.daysLeft ?? 99999) - (b.daysLeft ?? 99999));

	// ── Scouting (pipeline salvata dal modulo scouting) ─────────────────────
	const pipeline = scoutingRes.pipeline;
	const observedMatches = [
		...new Map(
			[...(matchesRes || []), ...(observedRes || [])].map((m) => [m.id, m]),
		).values(),
	]
		.filter((m) => m.scoutingPlanned && isMatchScheduled(m, now))
		.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

	// ── Economia (budget/movimenti del modulo Finanza) ──────────────────────
	const budget = readDsPreference(BUDGET_KEY, null);
	const movements = readDsPreference(MOVEMENTS_KEY, []) || [];

	return {
		loadedAt: now.toISOString(),
		season,
		seasonStart,
		seasonEnd,
		missingSources: [],
		teams,
		...membershipIndex,
		activeInjuries,
		activeInjuryEpisodes,
		medicalPlayerIds,
		seasonInjuries: clubSeasonInjuries,
		contracts: contractRows,
		contractsByPlayer: indexDsContracts(contractRows),
		...summarizeDsContractCosts(contractRows),
		staff,
		matches,
		trainings,
		trainingsWeek: pastTrainings.length,
		feesPreview,
		scoutReports: scoutReportsRes || [],
		scoutingReports: scoutingRes.reports,
		scoutingVisions: scoutingRes.visions,
		developmentRosters: scoutingRes.rosters,
		users: usersRes || [],
		pipeline,
		observedMatches,
		venues: venuesRes || [],
		finance: {
			budget: typeof budget === "number" ? budget : null,
			movements: Array.isArray(movements) ? movements : [],
		},
	};
};

// ── Filtro squadra ──────────────────────────────────────────────────────────
// La fotografia ristretta a una squadra ha la stessa forma di quella del club:
// le pagine e i calcoli funzionano senza sapere se il filtro è attivo.

const TEAM_FILTER_KEY = "ds_team_filter_v1";

export const loadTeamFilter = () => readDsPreference(TEAM_FILTER_KEY);

export const saveTeamFilter = (teamId) =>
	writeDsPreference(TEAM_FILTER_KEY, teamId || null);

export const scopeSnapshot = (s, teamId) => {
	const team = teamId && s?.teams.find((t) => t.id === teamId);
	if (!team) return s;
	const ids = new Set(team.players.map((p) => p.id));
	const seasonInjuries = s.seasonInjuries.filter((i) => ids.has(i.playerId));
	const withTeam = (entry) => ({...entry, team, teams: [team]});
	const contracts = s.contracts
		.filter((c) => ids.has(c.playerId))
		.map(withTeam);
	return {
		...s,
		full: s.full || s,
		teamScope: team.id,
		teams: [team],
		...indexDsMemberships([team]),
		activeInjuries: s.activeInjuries
			.filter((i) => ids.has(i.playerId))
			.map((entry) => ({
				...withTeam(entry),
				episodes: entry.episodes.map(withTeam),
			})),
		activeInjuryEpisodes: (s.activeInjuryEpisodes || [])
			.filter((i) => ids.has(i.playerId))
			.map(withTeam),
		medicalPlayerIds: new Set(
			[...(s.medicalPlayerIds || [])].filter((pid) => ids.has(pid)),
		),
		seasonInjuries,
		contracts,
		contractsByPlayer: indexDsContracts(contracts),
		...summarizeDsContractCosts(contracts),
		staff: team.staff,
		matches: s.matches.filter(
			(m) => m.homeRosterId === team.id || m.awayRosterId === team.id,
		),
		trainings: s.trainings.filter((t) => t.rosterId === team.id),
		trainingsWeek: team.trainingsWeek,
	};
};

// Certificati riferiti ai soli giocatori della fotografia (eventualmente filtrata)
export const scopeCertificates = (certificates, s) =>
	certificates && s
		? {
				...certificates,
				byPlayer: new Map(
					[...certificates.byPlayer].filter(([id]) => s.playerById.has(id)),
				),
			}
		: certificates;

export const loadClubSnapshot = async (season, {force = false} = {}) => {
	if (!season?.id || !isDsContextReady()) return null;
	const context = getDsContext();
	const key = context + "|" + season.id + "|" + getInternalClubId();
	if (
		!force &&
		snapshotCache?.key === key &&
		Date.now() - snapshotCache.at < CACHE_TTL_MS
	) {
		return snapshotCache.promise;
	}
	certificatesCache = null;
	agentsCache = null;
	const promise = fetchSnapshot(season, context).then((snapshot) => {
		assertDsContext(context);
		if (snapshotCache?.promise !== promise)
			throw new Error("Fotografia DS superata");
		return {...snapshot, context};
	});
	snapshotCache = {key, at: Date.now(), promise};
	promise.catch(() => {
		if (snapshotCache?.promise === promise) snapshotCache = null;
	});
	return promise;
};

// Certificati medico-sportivi: una chiamata per giocatore, caricati solo
// dalle pagine che li mostrano e poi riusati.
export const loadClubCertificates = async (snapshot, {force = false} = {}) => {
	if (!snapshot) return null;
	const context = getDsContext();
	assertDsContext(snapshot.context);
	if (!force && certificatesCache?.snapshot === (snapshot.full || snapshot)) {
		return certificatesCache.promise;
	}
	const playerIds = [...(snapshot.full || snapshot).playerById.keys()];
	const promise = Promise.all(
		playerIds.map((id) =>
			PlayerAPI.getCertificates(id)
				.then((certs) => [id, Array.isArray(certs) ? certs : [], true])
				.catch(() => [id, [], false]),
		),
	).then((rows) => {
		assertDsContext(context);
		if (certificatesCache?.promise !== promise)
			throw new Error("Certificati DS superati");
		const now = moment();
		const byPlayer = new Map();
		let failed = 0;
		rows.forEach(([id, certs, ok]) => {
			if (!ok) failed += 1;
			const res = certificateStatus(certs, now);
			byPlayer.set(id, {...res, status: ok ? res.status : "unknown"});
		});
		// Nessun certificato in tutto il club = modulo non ancora utilizzato
		const tracked = rows.some(([, certs]) => certs.length > 0);
		return {byPlayer, failed, total: rows.length, tracked};
	});
	certificatesCache = {snapshot: snapshot.full || snapshot, promise};
	return promise;
};

// Procuratori: storico agenti di ogni giocatore + dettaglio dei contratti
// (cartellino, lordo annuo, bonus), caricati solo dalle pagine che li mostrano.
export const loadClubAgents = async (snapshot) => {
	if (!snapshot) return null;
	const context = getDsContext();
	assertDsContext(snapshot.context);
	const base = snapshot.full || snapshot;
	if (agentsCache?.snapshot === base) return agentsCache.promise;
	const promise = Promise.all([
		Promise.all(
			[...base.playerById.keys()].map((id) =>
				PlayerAPI.getAgents(id)
					.then((rows) => [id, Array.isArray(rows) ? rows : []])
					.catch(() => [id, []]),
			),
		),
		Promise.all(
			base.contracts.map((c) =>
				ContractAPI.getById(c.contractId || c.id)
					.then((detail) => [c.contractId || c.id, detail])
					.catch(() => [c.contractId || c.id, null]),
			),
		),
	]).then(([agentRows, contractRows]) => {
		assertDsContext(context);
		if (agentsCache?.promise !== promise)
			throw new Error("Procuratori DS superati");
		const contractDetails = new Map(contractRows.filter(([, d]) => d));
		const byAgent = new Map();
		agentRows.forEach(([playerId, history]) => {
			const current = [...history].sort(
				(a, b) => moment(b.dateFrom) - moment(a.dateFrom),
			)[0];
			if (!current?.name) return;
			const key = current.name.trim().toLowerCase();
			if (!byAgent.has(key))
				byAgent.set(key, {id: key, name: current.name.trim(), clients: []});
			byAgent.get(key).clients.push({
				playerId,
				since: current.dateFrom,
				notes: current.notes,
			});
		});
		return {
			agents: [...byAgent.values()],
			contractDetails,
			tracked: byAgent.size > 0,
		};
	});
	agentsCache = {snapshot: base, promise};
	promise.catch(() => {
		if (agentsCache?.promise === promise) agentsCache = null;
	});
	return promise;
};

// Fabbisogni: reparti sotto soglia oggi o che lo diventano a fine stagione
// considerando i contratti in scadenza.
export const buildNeeds = (s) => {
	const seasonEnd = moment(s.seasonEnd);
	const needs = [];
	s.teams.forEach((team) => {
		if (team.players.length < 10) return;
		DEPARTMENTS.forEach((d) => {
			const players = team.players.filter((p) => getDepartment(p) === d.id);
			const leaving = players.filter((p) => {
				const list = s.contractsByPlayer?.get(p.id) || [];
				return (
					list.length > 0 &&
					list.every(
						(c) => c.endDate && moment(c.endDate).isSameOrBefore(seasonEnd),
					)
				);
			});
			const now = players.length;
			const next = now - leaving.length;
			if (now < d.min || next < d.min) {
				needs.push({
					id: team.id + d.id,
					experimental: true,
					team,
					department: d,
					now,
					next,
					leaving,
					missing: Math.max(0, d.min - next),
					severity: now < d.min ? "alert" : "warn",
				});
			}
		});
	});
	return needs.sort((a, b) => b.missing - a.missing);
};

// Sintesi DS concordata: una misura per gara prima di media e picco.
export const summarizeDsGps = (rows = []) => {
	const sumKeys = new Set([
		"totalDistance",
		"distance16kmh",
		"distance1525kmh",
		"distance2025kms",
		"distance25kmh",
		"nAccelerations3ms",
		"nDecelerations3ms",
		"distAccelerations3ms",
		"distDecelerations3ms",
		"jumps",
		"diveRx",
		"diveLx",
	]);
	const maxKeys = new Set(["maxSpeed", "amax"]);
	const games = new Map();
	let unidentified = 0;
	rows.forEach((row) => {
		if (!row.matchId) {
			unidentified++;
			return;
		}
		if (!games.has(row.matchId)) games.set(row.matchId, []);
		games.get(row.matchId).push(row);
	});
	const value = (row, key) =>
		typeof row?.[key] === "number" && Number.isFinite(row[key])
			? row[key]
			: null;
	let excludedGames = 0;
	const perGame = [...games.values()]
		.map((records) => {
			const total = records.filter((r) => r.fraction === 0);
			const first = records.filter((r) => r.fraction === 1);
			const second = records.filter((r) => r.fraction === 2);
			if (
				total.length > 1 ||
				(!total.length &&
					(first.length !== 1 ||
						second.length !== 1 ||
						records.some((r) => ![1, 2].includes(r.fraction))))
			) {
				excludedGames++;
				return null;
			}
			return Object.fromEntries(
				GPS_STAT_METRICS.map(({key}) => {
					if (total.length === 1) return [key, value(total[0], key)];
					const a = value(first[0], key),
						b = value(second[0], key);
					return [
						key,
						a === null || b === null
							? null
							: sumKeys.has(key)
								? a + b
								: maxKeys.has(key)
									? Math.max(a, b)
									: null,
					];
				}),
			);
		})
		.filter(Boolean);
	const metrics = GPS_STAT_METRICS.filter(({key}) =>
		rows.some((row) => row[key] != null),
	).map((metric) => {
		const values = perGame
			.map((game) => game[metric.key])
			.filter((v) => v !== null);
		return {
			...metric,
			known: values.length,
			total: games.size,
			mean: values.length
				? values.reduce((a, b) => a + b, 0) / values.length
				: null,
			peak: values.length ? Math.max(...values) : null,
		};
	});
	return {metrics, games: games.size, excludedGames, unidentified};
};

export const summarizeClubWellness = (teams = []) => {
	if (teams.length === 1) return teams[0].wellness;
	const players = [
		...new Map(
			teams
				.flatMap((t) => t.wellness.players)
				.map((w) => [w.player.id, w.player]),
		).values(),
	];
	const unique = (key, date, value) => [
		...new Map(
			teams
				.flatMap((t) => t.wellness[key] || [])
				.map((r) => [
					r.id ||
						[r.playerId, r[date], r[value], r.matchId, r.trainingId].join("|"),
					r,
				]),
		).values(),
	];
	const reference = teams[0]?.wellness;
	return summarizeDsWellness(
		players,
		unique("sourceTqr", "tqrDate", "tqrValue"),
		unique("sourceRpe", "rpeDate", "rpe"),
		reference?.fromDate,
		reference?.toDate,
		moment(),
	);
};

export const summarizeDsAge = (players, now = moment()) => {
	const unique = [...new Map((players || []).map((p) => [p.id, p])).values()];
	const ages = unique
		.filter(
			(p) =>
				p.dateOfBirth &&
				moment(p.dateOfBirth).isValid() &&
				moment(p.dateOfBirth).isSameOrBefore(now),
		)
		.map((p) => moment(now).diff(moment(p.dateOfBirth), "years"));
	return {
		value: ages.length ? ages.reduce((a, b) => a + b, 0) / ages.length : null,
		known: ages.length,
		total: unique.length,
	};
};

export const summarizeDsMedicalDays = (snapshot, playerIds, now = moment()) => {
	const ids = new Set(playerIds || snapshot?.playerById?.keys() || []);
	const start = moment(
		snapshot?.season?.startDate || snapshot?.seasonStart,
	).startOf("day");
	const seasonEnd = moment(
		snapshot?.season?.endDate || snapshot?.seasonEnd || null,
	).startOf("day");
	const today = moment(now).startOf("day");
	const end = seasonEnd.isValid() ? moment.min(today, seasonEnd) : today;
	if (
		!snapshot ||
		!(snapshot.season?.startDate || snapshot.seasonStart) ||
		!start.isValid() ||
		end.isBefore(start)
	)
		return {
			days: null,
			availability: null,
			known: 0,
			total: ids.size,
			elapsed: 0,
		};
	const elapsed = end.diff(start, "days") + 1;
	const byPlayer = new Map(
		[...ids]
			.filter((id) => snapshot.medicalPlayerIds?.has(id))
			.map((id) => [id, []]),
	);
	const invalid = new Set();
	(snapshot.seasonInjuries || []).forEach((injury) => {
		if (injury.recordType || !byPlayer.has(injury.playerId)) return;
		const from = moment(injury.startDate).startOf("day");
		const discharge = injury.sportsDischargeDate
			? moment(injury.sportsDischargeDate).startOf("day")
			: null;
		if (
			!injury.startDate ||
			!from.isValid() ||
			(discharge && (!discharge.isValid() || discharge.isBefore(from)))
		) {
			invalid.add(injury.playerId);
			return;
		}
		const left = moment.max(start, from).diff(start, "days");
		const right = moment
			.min(end, discharge ? discharge.clone().subtract(1, "day") : end)
			.diff(start, "days");
		if (right >= left) byPlayer.get(injury.playerId).push([left, right]);
	});
	let days = 0,
		known = 0;
	byPlayer.forEach((intervals, id) => {
		if (invalid.has(id)) return;
		known++;
		const merged = [];
		intervals
			.sort((a, b) => a[0] - b[0])
			.forEach(([left, right]) => {
				const last = merged[merged.length - 1];
				if (last && left <= last[1] + 1) last[1] = Math.max(last[1], right);
				else merged.push([left, right]);
			});
		days += merged.reduce((n, [left, right]) => n + right - left + 1, 0);
	});
	return {
		days: known ? days : null,
		availability: known ? 100 * (1 - days / (known * elapsed)) : null,
		known,
		total: ids.size,
		elapsed,
		fromDate: start.format("YYYY-MM-DD"),
		toDate: end.format("YYYY-MM-DD"),
	};
};

// Stesso perimetro di Finanza: elenco expiring del backend, non contratto scelto per persona.
export const normalizeDsFinanceContracts = (rows = []) => [
	...new Map(
		(rows || [])
			.filter((c) => (c.daysToExpiry ?? 1) >= 0)
			.map((c) => [c.contractId || c.id, c]),
	).values(),
];
export const indexDsContracts = (rows = []) => {
	const byPlayer = new Map();
	rows.forEach((c) => {
		if (!byPlayer.has(c.playerId)) byPlayer.set(c.playerId, []);
		byPlayer.get(c.playerId).push(c);
	});
	return byPlayer;
};
export const summarizeDsContractCosts = (rows = []) => {
	const values = rows
		.map((c) => c.totalMonthlySalary)
		.filter((v) => typeof v === "number" && Number.isFinite(v));
	return {
		monthlySalary:
			rows.length && !values.length ? null : values.reduce((a, b) => a + b, 0),
		salaryKnown: values.length,
		salaryCount: rows.length,
	};
};
export const annualizedDsSalary = (value) =>
	value == null ? null : value * 12;
export const dsSalaryCoverage = (s) =>
	`${s?.salaryKnown ?? 0}/${s?.salaryCount ?? 0} importi rilevati${s?.salaryKnown < s?.salaryCount ? " · subtotale parziale" : ""}`;
export const dsRegisteredSubtotal = (rows) => {
	const known = rows.filter(
		(r) =>
			!r.preview && typeof r.value === "number" && Number.isFinite(r.value),
	);
	return {
		value: known.length ? known.reduce((n, r) => n + r.value, 0) : null,
		known: known.length,
		total: rows.filter((r) => !r.preview).length,
	};
};

// Rosa all'inizio di una stagione futura (1° luglio): esce chi ha un contratto
// che finisce prima, resta chi non ha contratto (stessa regola di buildNeeds)
export const buildSeasonPlan = (s, startYear) => {
	const seasonStart = moment(startYear + "-07-01");
	const rows = [];
	s.teams.forEach((team) => {
		if (team.players.length < 10) return;
		DEPARTMENTS.forEach((d) => {
			const players = team.players.filter((p) => getDepartment(p) === d.id);
			const leaving = players.filter((p) => {
				const list = s.contractsByPlayer?.get(p.id) || [];
				return (
					list.length > 0 &&
					list.every(
						(c) => c.endDate && moment(c.endDate).isBefore(seasonStart, "day"),
					)
				);
			});
			const staying = players.length - leaving.length;
			rows.push({
				id: team.id + d.id,
				team,
				department: d,
				staying,
				leaving,
				severity:
					staying >= d.min ? "ok" : staying >= d.min - 1 ? "warn" : "alert",
			});
		});
	});
	return rows;
};
